import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import DataTable from "../../components/DataTable/DataTable";
import {API} from "../../types/API";
import {DataTableRequest} from "../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../components/DataTable/TD";
import {ErrorHandler} from "../../hooks/errorHandler";
import {useToastContext} from "../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import TableSkeleton from "../../components/Skeletons/TableSkeleton";
import {useParams} from "react-router-dom";
import CountryAvatar from "../../components/CountryAvatar";
import {useTranslation} from "react-i18next";
import {TranslationsResponse} from "../../types/Responses/TranslationsResponse";
import {useSystemContext} from "../../types/contexts/SystemContext";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useUserContext} from "../../types/contexts/UserContext";
import Loader from "../../assets/icons/Loader";
import Card from "../../components/Card";
import {setTitle} from "../../hooks/setTitle";
import {APILink} from "../../types/APILink";
import {checkEnabled2FA} from "../../hooks/checkEnabled2FA";
import usePersistedDataTableRequest from "../../hooks/usePersistedDataTableRequest";
import {useDispatch} from "react-redux";
import {setSidebarTitle} from "../../reducers/sidebar";
import FilterChips, {FilterChipDefinition} from "../../components/FilterChips";
import DropdownInput from "../../components/DropdownInput";

type TranslationsDataTableRequest = DataTableRequest & { language?: number };

const Translations = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const dispatch = useDispatch();
    const toast = useToastContext();
    const system = useSystemContext();
    const user = useUserContext();
    const handleError = useErrorHandler();
    const [translations, setTranslations] = useState<TranslationsResponse>();
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const hiddenFileInput = React.useRef<HTMLInputElement>(null);
    const defaultRequest = useMemo<TranslationsDataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<TranslationsDataTableRequest>(defaultRequest);
    const filterLanguage = dataTableRequest.language ?? 0;

    const handleClick = useCallback(() => {
        // @ts-expect-error can be undefined
        hiddenFileInput.current.click();
    }, [hiddenFileInput]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const {files} = event.target;
        if (files && files.length > 0) {
            setIsUploading(true);
            API.uploadTranslations(files[0]).then((response: AxiosResponse) => {
                setIsUploading(false);
                if (response.status === 200)
                    window.location.reload();
                console.log(response.status === 200, response.status);
            }).catch((e) => {
                setIsUploading(false);
                ErrorHandler(e, `post${APILink.UPLOAD_TRANSLATIONS}`, toast, handleError)
            });
        }
        // @ts-expect-error can be undefined
        hiddenFileInput.current.value = '';
    }, [hiddenFileInput]);


    const clearRequest = (request: TranslationsDataTableRequest | object) => {
        const keys = Object.keys(request);
        if (keys.includes('language') && typeof request['language'] !== "undefined" && parseInt(request['language']) === 0)
            delete request['language'];
        return request;
    }


    const sendRequest = useCallback((request: TranslationsDataTableRequest | object) => {
        setIsDataTableLoading(true);
        API.getTranslations(clearRequest(request)).then((response: AxiosResponse) => {
            setTranslations(response.data as TranslationsResponse);
            setIsDataTableLoading(false);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_TRANSLATIONS}`, toast, handleError));
    }, [toast, handleError]);

    const onDataTableChanged = useCallback((request: DataTableRequest) => {
        const nextRequest: TranslationsDataTableRequest = {
            ...request,
            language: filterLanguage,
        }
        persistDataTableRequest(nextRequest);
        if (!nextRequest.search || nextRequest.search.length > 3) {
            sendRequest(!nextRequest.search ? {...nextRequest, search: ''} : nextRequest);
        }
    }, [persistDataTableRequest, filterLanguage, sendRequest]);

    const onLanguageChanged = useCallback((u: string | number) => {
        const parsedLanguage = typeof u === "string" ? parseInt(u) : u;
        const request: TranslationsDataTableRequest = {
            ...dataTableRequest,
            page: 1,
            language: parsedLanguage,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [dataTableRequest, sendRequest, persistDataTableRequest]);

    const download = useCallback(() => {
        setIsDownloading(true);
        API.downloadTranslations().then((response: AxiosResponse) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', "translations.csv");

            document.body.appendChild(link);
            link.click();

            // @ts-expect-error can be undefined
            link.parentNode.removeChild(link);
            setIsDownloading(false);
        }).catch((e) => {
            setIsDownloading(false);
            ErrorHandler(e, `get${APILink.GET_TRANSLATIONS}`, toast, handleError)
        });
    }, []);

    useEffect(() => {
        setTitle(t('admin.translations.title'));
        dispatch(setSidebarTitle(t('admin.translations.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                let request = {...dataTableRequest, language: filterLanguage};
                const locationSearch = window.location.search.substr(1).split('&');
                if (locationSearch.length > 0) {
                    const newID = locationSearch[0]?.split('=');
                    if (newID?.length) {
                        request = {...request, search: newID[1]};
                        persistDataTableRequest(request);
                    }
                }
                sendRequest(request);
            }).catch()
        }
    }, [user, id, dispatch]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);


    const filterChipsItems = useMemo<FilterChipDefinition[]>(() => {
        const chips: FilterChipDefinition[] = [];
        if (system?.system?.languages && filterLanguage > 0) {
            const language = system.system.languages.find((lang) => lang.id === filterLanguage);
            if (language) {
                chips.push({
                    key: `language-${language.id}`,
                    label: `${t('admin.translations.table.language')}: ${language.name}`,
                    onRemove: () => onLanguageChanged('0')
                });
            }
        }
        return chips;
    }, [system, filterLanguage, t, onLanguageChanged]);

    const filters = useMemo(() => {
        if (!system || !system.system || !system.system.languages || !user || !user.user)
            return <></>;

        return <>
            <DropdownInput
                withAllButton={true}
                searchable={true}
                menuClassName="max-w-[9.3rem] min-w-[9.3rem]"
                name={t('admin.users.table.language')}
                selected={filterLanguage}
                onChange={onLanguageChanged}
                list={system.system.languages.map((i) => {
                    return {
                        ...i,
                        icon: <CountryAvatar country={i} withName={false} className="h-3 max-w-[1rem]"/>
                    }
                })}
                onSelectAll={() => onLanguageChanged(0)}
            />
        </>
    }, [filterLanguage, system, onLanguageChanged, user, t]);

    const rightPanel = useMemo(() => {
        if (!user || !user.user)
            return <></>
        return <div className="flex flex-row gap-2 justify-end">
            <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                    onClick={() => refresh()}>
                <FontAwesomeIcon
                    icon={solid('refresh')}
                    className="w-12 h-3"/>
            </Button>

            {user.user.permissions.includes('export translations') &&
                <Button className="bg-black px-0.5" onClick={() => download()}>
                    {isDownloading ? <Loader width="w-12 h-3"/> :
                        <FontAwesomeIcon icon={solid('download')} className="w-12 h-3"/>}

                </Button>}
            <input type="file"
                   accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                   ref={hiddenFileInput} onChange={handleChange}
                   style={{display: 'none'}}/>
            {user.user.permissions.includes('import translations') &&
                <Button className="bg-black px-0.5" onClick={() => handleClick()}>
                    {isUploading ? <Loader width="w-12 h-3"/> :
                        <FontAwesomeIcon icon={solid('upload')} className="w-12 h-3"/>}
                </Button>}
        </div>
    }, [user, hiddenFileInput,isDownloading, isUploading, handleClick, download, handleChange]);

    const dataTable = useMemo(() => {
        if (!translations)
            return <TableSkeleton/>;

        return <DataTable {...dataTableRequest}
                          total_pages={translations.pages.total}
                          count={translations.count}
                          nodata={translations.data.length === 0}
                          filters={filters}
                          fullWidthFilters={<FilterChips className="px-4 -mt-6 mb-6" items={filterChipsItems}/>}
                          inProgress={isDataTableLoading}
                          rightPanel={rightPanel}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t('admin.translations.table.language'), sortBy: 'language_id'},
            {name: t('admin.translations.table.key'), sortBy: 'translation_key'},
            {name: t('admin.translations.table.value'), sortBy: 'translation_text'},
        ]}>
            <tbody>
            {translations.data.map((translation, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD><CountryAvatar country={translation.language} withName={true}
                                           className="h-3 max-w-[1rem]"/></TD>
                        <TD>{translation.key}</TD>
                        <TD className="truncate max-w-[60%]">{translation.value}</TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, rightPanel, isDataTableLoading, translations, filters, filterChipsItems, onDataTableChanged]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {dataTable}
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Translations);
